'use client';

import { useCallback } from 'react';

import { useRouter } from 'next/navigation';

import { ShareButton } from '@ps/features/sharing/ShareButton';
import type { BioContext } from '@ps/functions/bio/bioContext';
import { bioUrl } from '@ps/functions/bio/bioUrls';
import { ArrowLeft, Share2 } from 'lucide-react';

interface Props {
  title: string;
  /** Public bio url code — the cold-load Back fallback target (`/<urlCode>`). */
  bioContext: BioContext;
  /** The collection being shared. */
  collectionId: string;
  /**
   * Back override. Defaults to `router.back()` / the bio home. The
   * `/my-popstore` state-driven aside passes a state-clearing callback since it
   * has no route to pop.
   */
  onBack?: () => void;
}

/**
 * Visitor collection-detail top bar — back arrow, centered collection name,
 * share. Client-only for the two interactions (Back + Share); the surrounding
 * page is server-rendered. Back uses `router.back()` when there's in-app history
 * (the intercept overlay / warm nav) and otherwise lands on the public bio.
 */
export function CollectionDetailHeader({
  title,
  bioContext,
  collectionId,
  onBack,
}: Props) {
  const router = useRouter();

  const handleBack = useCallback(() => {
    if (onBack) {
      onBack();
      return;
    }
    if (typeof window !== 'undefined' && window.history.length > 1) {
      router.back();
      return;
    }
    router.push(bioUrl(bioContext).home());
  }, [onBack, router, bioContext]);

  return (
    <header className="border-raise/10 flex w-full shrink-0 items-center justify-between border-b px-3 py-3">
      <button
        type="button"
        onClick={handleBack}
        aria-label="Back"
        className="text-content-1 flex h-7 w-7 items-center justify-center"
      >
        <ArrowLeft className="h-5 w-5" aria-hidden />
      </button>
      <h1 className="text-content-1 text-sub line-clamp-1 flex-1 px-2 text-center">
        {title}
      </h1>
      <ShareButton
        bioContext={bioContext}
        target={{ kind: 'collection', id: collectionId }}
        title={title}
        aria-label={`Share ${title}`}
        className="text-content-1 flex h-7 w-7 items-center justify-center"
      >
        <Share2 className="h-5 w-5" aria-hidden />
      </ShareButton>
    </header>
  );
}
